import React, { useEffect, useRef, useState } from 'react';
import { BarChart3, Layers, ShieldCheck, CalendarClock, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { OutputEstimator } from '../components/OutputEstimator';
import { PaymentSchedule } from '../components/PaymentSchedule';
import { AdvantageStructure } from '../components/AdvantageStructure';
import { AdvantageGuarantees } from '../components/AdvantageGuarantees';
import { usePricing } from '../contexts/PricingContext';
export function AdvantageProduct() {
  const {
    adSpend,
    cpaLow,
    cpaHigh,
    hasRecruiter,
    hasLeader,
    splitBuildFee,
    setAdSpend,
    setCpaLow,
    setCpaHigh,
    setHasRecruiter,
    setHasLeader,
    setSplitBuildFee,
    leaderBuildFee,
    leaderMonthlyFee,
    leaderActiveFee,
    leaderAdSpendLow,
    leaderAdSpendHigh,
    buildFee,
    scopeMultiplier,
    platformFee,
    campaignFee,
    recruiterFee,
    monthlyTotal,
    hiresLow,
    hiresHigh,
    pricingModel,
    setPricingModel
  } = usePricing();
  const [showPayments, setShowPayments] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [revealed, setRevealed] = useState(1);
  const [pendingScroll, setPendingScroll] = useState<number | null>(null);
  const slideRefs = useRef<Array<HTMLElement | null>>([]);
  const heroRef = useRef<HTMLElement | null>(null);
  const [heroHeight, setHeroHeight] = useState(0);

  // The first panel fills whatever is left below the hero, at any window size.
  useEffect(() => {
    const element = heroRef.current;
    if (!element) return;
    const measure = () => setHeroHeight(element.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const slides = [{
    label: 'Output and Ad Spend',
    icon: BarChart3,
    content: <OutputEstimator adSpend={adSpend} cpaLow={cpaLow} cpaHigh={cpaHigh} onAdSpendChange={setAdSpend} onCpaLowChange={setCpaLow} onCpaHighChange={setCpaHigh} hasLeader={hasLeader} data-id="element-1759" data-mp="206dqrcj3tuab" />
  }, {
    label: 'Partnership Structure',
    icon: Layers,
    content: <AdvantageStructure buildFee={buildFee} monthlyTotal={monthlyTotal} platformFee={platformFee} campaignFee={campaignFee} recruiterFee={recruiterFee} scopeMultiplier={scopeMultiplier} hasRecruiter={hasRecruiter} onRecruiterChange={setHasRecruiter} hasLeader={hasLeader} onLeaderChange={setHasLeader} leaderBuildFee={leaderBuildFee} leaderMonthlyFee={leaderMonthlyFee} leaderAdSpend={leaderAdSpendLow} splitBuildFee={splitBuildFee} onSplitBuildFeeChange={setSplitBuildFee} adSpend={adSpend} onAdSpendChange={setAdSpend} cpaLow={cpaLow} cpaHigh={cpaHigh} data-id="element-1760" data-mp="3cc7m4za6et8p" />
  }, {
    label: showPayments ? 'Timelines and Payments' : 'Timelines',
    icon: CalendarClock,
    content: <div data-id="element-1761" data-mp="gaudn72rjuyr">
          <PaymentSchedule buildFee={buildFee} monthlyService={monthlyTotal} adSpend={adSpend} hiresLow={hiresLow} hiresHigh={hiresHigh} splitBuildFee={splitBuildFee} onSplitBuildFeeChange={setSplitBuildFee} showPayments={showPayments} onShowPaymentsChange={setShowPayments} hasLeader={hasLeader} leaderBuildFee={leaderBuildFee} leaderMonthlyFee={leaderMonthlyFee} leaderActiveFee={leaderActiveFee} leaderAdSpendLow={leaderAdSpendLow} leaderAdSpendHigh={leaderAdSpendHigh} data-id="element-1762" data-mp="2de8dyw7e78l" />
        </div>
  }, {
    label: 'Guarantees and Agreements',
    icon: ShieldCheck,
    content: <AdvantageGuarantees data-id="element-1763" data-mp="1itqtrx00mpdn" />
  }];

  // Panels stay in the document once revealed, so scrolling back up shows the
  // earlier slides. Navigating reveals up to the target and scrolls to it.
  const goToSlide = (index: number) => {
    setRevealed(current => Math.max(current, index + 1));
    setActiveSlide(index);
    setPendingScroll(index);
  };

  // Arrow keys / page keys advance and rewind the deck one panel per press.
  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === 'PageDown';
      const backward = event.key === 'ArrowLeft' || event.key === 'ArrowUp' || event.key === 'PageUp';
      if (!forward && !backward) {
        return;
      }
      event.preventDefault();
      setActiveSlide(current => {
        const next = Math.min(slides.length - 1, Math.max(0, current + (forward ? 1 : -1)));
        if (next !== current) {
          setRevealed(revealedCount => Math.max(revealedCount, next + 1));
          setPendingScroll(next);
        }
        return next;
      });
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [slides.length]);
  useEffect(() => {
    if (pendingScroll === null) {
      return;
    }
    const node = slideRefs.current[pendingScroll];
    if (node) {
      node.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
    setPendingScroll(null);
  }, [pendingScroll, revealed]);
  return <div className="min-h-screen w-full bg-[#0A0F1C] text-slate-200 selection:bg-[#7FE0C3]/30 font-sans" data-id="element-1764" data-mp="21s4ppe2d7vn9">
      {/* 1. HERO */}
      <section ref={heroRef} className="relative pt-16 pb-0 px-6 md:px-12 max-w-7xl mx-auto text-center" data-id="element-1765" data-mp="25tzpge4ne0yx">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-96 bg-[#7FE0C3]/5 blur-[120px] rounded-full pointer-events-none" data-id="element-1766" data-mp="6m2dgvxo9yb6" />

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-1767" data-mp="fh094as7fp67">
          {/* Presenter-only: clicking the badge swaps between the default
              monthly model and the flagship (implementation fee) model.
              Deliberately no cursor, hover, or focus cue. */}
          <div onClick={() => setPricingModel(pricingModel === 'flagship' ? 'monthly' : 'flagship')} className="inline-block px-3 py-1 rounded-full bg-[#7FE0C3]/10 border border-[#7FE0C3]/20 text-[#7FE0C3] text-xs font-bold uppercase tracking-widest mb-4 select-none" data-id="element-1768" data-mp="yd9xbqwtlw3x">
            Flagship Partnership
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 tracking-tight" data-id="element-1769" data-mp="2q312j1xahljs">
            D2D HIRE Advantage
          </h1>
          <p className="text-xl md:text-2xl text-slate-400 max-w-3xl mx-auto leading-relaxed" data-id="element-1770" data-mp="2cakypywkdjht">
            D2D HIRE Advantage is the partnership where recruiting becomes
            consistent, predictable, and scalable.
          </p>
        </motion.div>
      </section>

      {/* 2. THE DECK */}
      <div className="relative" data-id="element-1771" data-mp="27dm4lgcwhcjm">
        {/* Icon rail — floats at the left edge without displacing the slides */}
        <nav aria-label="Partnership deck" className="hidden lg:flex fixed left-3 top-1/2 -translate-y-1/2 z-20 flex-col gap-1" data-id="element-1772" data-mp="1te36lgdszhl1">
          {slides.map((slide, index) => {
          const Icon = slide.icon;
          const isActive = index === activeSlide;
          return <button key={slide.label} type="button" onClick={() => goToSlide(index)} aria-current={isActive ? 'step' : undefined} aria-label={slide.label} title={slide.label} className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] ${isActive ? 'bg-[#7FE0C3]/10 text-[#7FE0C3]' : 'text-slate-700 hover:text-slate-300'}`} data-id="element-1773" data-mp="3sp4d2glupfec" data-mpi={index}>
                <Icon className="w-5 h-5" data-id="element-1774" data-mp="heba47wp4afz" />
              </button>;
        })}
        </nav>

        {slides.map((slide, index) => {
        if (index >= revealed) {
          return null;
        }
        const nextSlide = slides[index + 1];
        return <section key={slide.label} ref={node => {
          slideRefs.current[index] = node;
        }} aria-label={slide.label} className={`flex flex-col justify-center px-6 md:px-12 ${index === 0 ? 'pt-2 pb-8' : 'min-h-screen py-12'}`} style={index === 0 && heroHeight ? {
          minHeight: `calc(100vh - ${heroHeight}px)`
        } : undefined} data-id="element-1775" data-mp="26usrzl45y6kf" data-mpi={index}>
              <div className="w-full max-w-[1400px] mx-auto" data-id="element-1776" data-mp="2ckx1cpl5dds">
                {/* Icon row for small screens */}
                <div className="flex lg:hidden justify-center gap-1 mb-6" data-id="element-1777" data-mp="31shwaazuws9t">
                  {slides.map((item, itemIndex) => {
                const Icon = item.icon;
                return <button key={item.label} type="button" onClick={() => goToSlide(itemIndex)} aria-label={item.label} className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] ${itemIndex === index ? 'bg-[#7FE0C3]/10 text-[#7FE0C3]' : 'text-slate-600 hover:text-slate-300'}`} data-id="element-1778" data-mp="3ard39paq66e6" data-mpi={itemIndex}>
                        <Icon className="w-5 h-5" data-id="element-1779" data-mp="2n87h904kyoh8" />
                      </button>;
              })}
                </div>

                {slide.content}

                {nextSlide && <div className="mt-10 flex justify-center" data-id="element-1780" data-mp="1zclb9dnya0ns">
                    <button type="button" onClick={() => goToSlide(index + 1)} className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest text-slate-400 border border-slate-800 hover:border-[#7FE0C3]/40 hover:text-white transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1781" data-mp="1zefvepredoqt">
                      {nextSlide.label}
                      <ChevronDown className="w-4 h-4" data-id="element-1782" data-mp="3opq51hnewvgv" />
                    </button>
                  </div>}
              </div>
            </section>;
      })}
      </div>

    </div>;
}